import { DEFAULT_THEME, themeById, type Theme } from "@shared/themes";

// A theme changes type as well as colour, so its fonts have to arrive with it.
// Loading all six families up front would cost far more than any room needs, so
// each is fetched the first time a theme that uses it is applied.
const loadedFonts = new Set<string>();

function loadFonts(theme: Theme): void {
  const missing = theme.fonts.filter((f) => !loadedFonts.has(f));
  if (missing.length === 0) return;

  for (const f of missing) loadedFonts.add(f);
  const link = document.createElement("link");
  link.rel = "stylesheet";
  // `display=swap` so the theme applies instantly and the face swaps in when
  // it lands — a blank screen waiting on a webfont is worse than a reflow.
  link.href = `https://fonts.googleapis.com/css2?${missing
    .map((f) => `family=${f}`)
    .join("&")}&display=swap`;
  document.head.appendChild(link);
}

/**
 * Apply a theme. Every token is a custom property on <html>, so this is a
 * handful of writes and nothing needs to re-render.
 */
export function applyTheme(themeId: string | undefined | null): void {
  const theme = themeById(themeId ?? DEFAULT_THEME);
  const root = document.documentElement;

  for (const [key, value] of Object.entries(theme.vars)) {
    root.style.setProperty(key, value);
  }

  // Light themes need the browser's own UI — form controls, scrollbars, the
  // caret — to flip too.
  root.style.colorScheme = theme.scheme;
  root.dataset.theme = theme.id;
  root.dataset.scheme = theme.scheme;

  loadFonts(theme);

  // Keep the mobile browser chrome in step.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = theme.scheme === "light" ? "#ffffff" : "#04063f";
  }
}
